import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Mail, Megaphone, Save, ShieldAlert, Wrench } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

type PlatformSettingsData = {
  support_email: string;
  maintenance_mode: boolean;
  signup_enabled: boolean;
  announcement: string;
  updated_at?: string;
};

const defaultSettings: PlatformSettingsData = {
  support_email: 'support@thepolishedversion.com',
  maintenance_mode: false,
  signup_enabled: true,
  announcement: '',
};

/**
 * Platform settings (ROADMAP PA-0.4).
 *
 * The signup, maintenance and announcement values are persisted but are NOT yet
 * wired to any real application path — provisioning does not read `signup_enabled`,
 * routing does not read `maintenance_mode`, and nothing renders `announcement`.
 * Presenting them as live switches gave false confidence, so each is shown in a
 * read-only "Not currently enforced" state until PA-5.3 makes it real.
 *
 * The intended public support-contact email can be maintained as a stored record here. Email
 * *delivery* configuration — Resend API keys and the verified sending domain — is
 * deliberately absent: those credentials live in server secrets, never on this page.
 */

// A small pill making the inert status of a control impossible to misread.
const NotEnforcedBadge = () => (
  <Badge variant="outline" className="shrink-0 gap-1.5 border-amber-500/40 text-amber-600 dark:text-amber-400">
    <ShieldAlert className="h-3.5 w-3.5" />
    Not currently enforced
  </Badge>
);

const PlatformSettings = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [supportEmail, setSupportEmail] = useState(defaultSettings.support_email);

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_platform_settings' as never);
      if (error) throw error;
      return { ...defaultSettings, ...(data as Partial<PlatformSettingsData> || {}) };
    },
  });

  useEffect(() => {
    if (data) setSupportEmail(data.support_email);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Only the support-contact email is editable here. The inert values are passed
      // through unchanged from the server so an unenforced control can never be
      // "changed" as if it were operational (PA-5.3 will make them independently real).
      const { data: result, error } = await supabase.rpc('update_platform_settings' as never, {
        _support_email: supportEmail,
        _maintenance_mode: data?.maintenance_mode ?? defaultSettings.maintenance_mode,
        _signup_enabled: data?.signup_enabled ?? defaultSettings.signup_enabled,
        _announcement: data?.announcement ?? defaultSettings.announcement,
      } as never);
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      toast({ title: 'Support contact email saved' });
    },
    onError: () => toast({ title: 'Unable to save support contact email', variant: 'destructive' }),
  });

  const announcement = data?.announcement?.trim() ?? '';

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl text-foreground">Platform Settings</h2>
          <p className="mt-1 text-muted-foreground">
            The intended public support-contact email can be maintained as a stored contact record. No public
            surface currently publishes it automatically. The operational switches below are stored but not yet
            wired to the live platform, so they are shown read-only until they are properly enforced.
          </p>
        </div>
        <Badge variant="outline">Only platform admins</Badge>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading settings…</p> : null}
      {error ? <p className="text-sm text-destructive">Unable to load platform settings.</p> : null}

      {/* Editable stored contact record; this does not configure or imply email delivery. */}
      <Card>
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Mail className="h-5 w-5" />
          </div>
          <div className="space-y-1.5">
            <CardTitle>Support contact email</CardTitle>
            <CardDescription>
              The intended public address for people who need help. This page stores the contact record, but no
              public surface currently publishes it automatically, and it does not configure email delivery.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="support_email">Public support-contact email</Label>
            <Input
              id="support_email"
              type="email"
              autoComplete="off"
              value={supportEmail}
              onChange={(event) => setSupportEmail(event.target.value)}
            />
          </div>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || isLoading}>
            <Save className="mr-2 h-4 w-4" />
            Save support email
          </Button>
        </CardContent>
      </Card>

      {/* Explains — without exposing — the protected delivery/sender configuration. */}
      <Card>
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="space-y-1.5">
            <CardTitle>Email delivery &amp; sender configuration</CardTitle>
            <CardDescription>Separate from the public support-contact email above.</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            How the platform actually <span className="font-medium text-foreground">sends</span> email — the
            email-provider API keys (for example Resend) and the verified sender domain — is protected
            configuration. Those credentials are held only in server secrets and are deliberately never shown
            or entered on this page.
          </p>
          <p>
            Managing the sender identity and delivery provider is reserved for{' '}
            <span className="font-medium text-foreground">PA-5.3</span>, which will handle it through secure
            server-side configuration rather than this form.
          </p>
        </CardContent>
      </Card>

      {/* Inert controls — stored but not enforced. Read-only until PA-5.3. */}
      <Card>
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <div className="space-y-1.5">
            <CardTitle>Operational controls</CardTitle>
            <CardDescription>
              These values are stored but not yet connected to the live platform. They are read-only here so the
              page cannot imply an effect it does not have. Each will become a real, independently protected
              control in PA-5.3.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 rounded-lg border p-4 opacity-90 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="signup_enabled">New tenant signups</Label>
                <NotEnforcedBadge />
              </div>
              <p className="text-sm text-muted-foreground">
                Intended to make onboarding invite-only. Provisioning does not read this value yet, so it has no
                effect.
              </p>
            </div>
            <Switch id="signup_enabled" checked={data?.signup_enabled ?? false} disabled aria-readonly />
          </div>

          <div className="flex flex-col gap-3 rounded-lg border p-4 opacity-90 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Label htmlFor="maintenance_mode" className="flex items-center gap-1.5">
                  <Wrench className="h-4 w-4 text-muted-foreground" />
                  Maintenance mode
                </Label>
                <NotEnforcedBadge />
              </div>
              <p className="text-sm text-muted-foreground">
                Intended to show a holding page during planned maintenance. Routing does not read this value yet,
                so it has no effect.
              </p>
            </div>
            <Switch id="maintenance_mode" checked={data?.maintenance_mode ?? false} disabled aria-readonly />
          </div>

          <div className="rounded-lg border p-4 opacity-90">
            <div className="flex flex-wrap items-center gap-2">
              <Label className="flex items-center gap-1.5">
                <Megaphone className="h-4 w-4 text-muted-foreground" />
                Platform announcement
              </Label>
              <NotEnforcedBadge />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Intended for a platform-wide notice. Nothing renders this value yet, so it is shown here for
              reference only and cannot be edited.
            </p>
            <div
              className="mt-3 min-h-[3rem] rounded-md border border-dashed bg-muted/40 p-3 text-sm text-muted-foreground"
              aria-readonly
            >
              {announcement || 'No announcement set.'}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PlatformSettings;
